import { useEffect, useState } from 'react'
import { Check, Clock, Loader2, Pencil, PauseCircle, Play, Trash2, User, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { cn } from '@/lib/utils'
import {
  formatHeldCartAge,
  heldCartAgeMinutes,
  HELD_CART_SOFT_LIMIT,
  HELD_CART_STALE_MINUTES,
  type HeldCart,
} from './heldCarts'

export interface HeldOrdersSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  carts: HeldCart[]
  loading: boolean
  online: boolean
  formatCurrency: (n: number) => string
  orderTypeLabel: (id: string) => string
  orderTypeColor: (id: string) => string | undefined
  /** Current menu name for a product id; undefined when it is off the menu. */
  productName: (id: string) => string | undefined
  /** Id of the card whose resume / discard is in flight, if any. */
  busyId: string | null
  onResume: (cart: HeldCart) => void
  onDiscard: (cart: HeldCart) => Promise<void>
  onRename: (cart: HeldCart, label: string) => Promise<void>
}

/**
 * The "On hold" list (F-FE-N158). A slide-over so the menu and the rail stay
 * exactly where the cashier left them; resuming closes it and loads the cart.
 *
 * Discard is a two-tap inline confirm rather than a second dialog stacked on
 * this one — nothing here was sent to the kitchen or paid, so the record the
 * server keeps (who, when) is the safeguard, not a manager PIN.
 */
export function HeldOrdersSheet({
  open,
  onOpenChange,
  carts,
  loading,
  online,
  formatCurrency,
  orderTypeLabel,
  orderTypeColor,
  productName,
  busyId,
  onResume,
  onDiscard,
  onRename,
}: HeldOrdersSheetProps) {
  // Ages tick while the sheet is open so "4 min" does not freeze.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!open) return
    setNow(Date.now())
    const t = window.setInterval(() => setNow(Date.now()), 30_000)
    return () => window.clearInterval(t)
  }, [open])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" data-testid="held-orders-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <PauseCircle className="h-5 w-5 text-amber-600" aria-hidden />
            On hold ({carts.length})
          </SheetTitle>
          <SheetDescription>
            Orders parked while a guest decides. Resume one to keep adding items or take payment.
          </SheetDescription>
        </SheetHeader>
        <SheetBody className="space-y-3">
          {!online && (
            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
              Offline — held orders can be resumed again when the internet is back.
            </p>
          )}
          {carts.length >= HELD_CART_SOFT_LIMIT && (
            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
              {carts.length} orders are on hold. Discard the ones whose guests have left.
            </p>
          )}
          {loading && carts.length === 0 && (
            <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
            </div>
          )}
          {!loading && carts.length === 0 && (
            <p className="py-10 text-center text-sm text-muted-foreground">Nothing is on hold.</p>
          )}
          {carts.map((cart) => (
            <HeldCartCard
              key={cart.id}
              cart={cart}
              now={now}
              online={online}
              busy={busyId === cart.id}
              anyBusy={busyId != null}
              formatCurrency={formatCurrency}
              typeLabel={orderTypeLabel(cart.order_type)}
              typeColor={orderTypeColor(cart.order_type)}
              productName={productName}
              onResume={() => onResume(cart)}
              onDiscard={() => onDiscard(cart)}
              onRename={(label) => onRename(cart, label)}
            />
          ))}
        </SheetBody>
      </SheetContent>
    </Sheet>
  )
}

function HeldCartCard({
  cart,
  now,
  online,
  busy,
  anyBusy,
  formatCurrency,
  typeLabel,
  typeColor,
  productName,
  onResume,
  onDiscard,
  onRename,
}: {
  cart: HeldCart
  now: number
  online: boolean
  busy: boolean
  anyBusy: boolean
  formatCurrency: (n: number) => string
  typeLabel: string
  typeColor: string | undefined
  productName: (id: string) => string | undefined
  onResume: () => void
  onDiscard: () => Promise<void>
  onRename: (label: string) => Promise<void>
}) {
  const [confirmDiscard, setConfirmDiscard] = useState(false)
  const [editing, setEditing] = useState(false)
  const [draftLabel, setDraftLabel] = useState(cart.label)
  const [saving, setSaving] = useState(false)

  const minutes = heldCartAgeMinutes(cart.held_at, now)
  const stale = minutes >= HELD_CART_STALE_MINUTES
  const lines = cart.snapshot?.cart ?? []
  const preview = lines.slice(0, 3).map((l) => `${l.quantity}× ${productName(l.productId) ?? 'Unavailable item'}`)
  const more = lines.length - preview.length
  const disabled = !online || anyBusy

  const saveLabel = async () => {
    const next = draftLabel.trim()
    if (!next || next === cart.label) {
      setEditing(false)
      setDraftLabel(cart.label)
      return
    }
    setSaving(true)
    try {
      await onRename(next)
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="overflow-hidden rounded-lg border border-border bg-card shadow-sm"
      style={typeColor ? { borderLeft: `4px solid ${typeColor}` } : undefined}
      data-testid="held-cart-card"
    >
      <div className="space-y-2 p-3">
        <div className="flex items-start justify-between gap-2">
          {editing ? (
            <form
              className="flex min-w-0 flex-1 items-center gap-1"
              onSubmit={(e) => {
                e.preventDefault()
                void saveLabel()
              }}
            >
              <Input
                autoFocus
                value={draftLabel}
                maxLength={80}
                onChange={(e) => setDraftLabel(e.target.value)}
                className="h-8"
                aria-label="Held order name"
              />
              <Button type="submit" size="icon" variant="ghost" className="h-8 w-8 shrink-0" disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              </Button>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="h-8 w-8 shrink-0"
                onClick={() => {
                  setEditing(false)
                  setDraftLabel(cart.label)
                }}
                aria-label="Cancel rename"
              >
                <X className="h-4 w-4" />
              </Button>
            </form>
          ) : (
            <div className="flex min-w-0 items-center gap-1">
              <span className="truncate font-semibold">{cart.label}</span>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="h-7 w-7 shrink-0 text-muted-foreground"
                onClick={() => {
                  setDraftLabel(cart.label)
                  setEditing(true)
                }}
                disabled={disabled}
                aria-label="Rename held order"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
          <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            {typeLabel}
          </span>
        </div>

        <ul className="space-y-0.5 text-sm text-foreground/90">
          {preview.map((p, i) => (
            <li key={i} className="truncate">
              {p}
            </li>
          ))}
          {more > 0 && <li className="text-xs text-muted-foreground">+{more} more</li>}
        </ul>

        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span className="tabular-nums">
            {cart.item_count} item{cart.item_count === 1 ? '' : 's'} · est. {formatCurrency(cart.estimated_total)}
          </span>
          <span className="flex items-center gap-2">
            <span
              className={cn(
                'flex items-center gap-1',
                stale && 'font-medium text-amber-700 dark:text-amber-400',
              )}
            >
              <Clock className="h-3 w-3" aria-hidden />
              {formatHeldCartAge(minutes)}
            </span>
            {cart.held_by_name && (
              <span className="flex items-center gap-1">
                <User className="h-3 w-3" aria-hidden />
                {cart.held_by_name}
              </span>
            )}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2 border-t border-border bg-muted/30 px-3 py-2">
        {confirmDiscard ? (
          <>
            <span className="flex-1 text-sm font-medium">Discard this held order?</span>
            <Button type="button" size="sm" variant="outline" onClick={() => setConfirmDiscard(false)} disabled={busy}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              disabled={disabled}
              onClick={() => {
                void onDiscard().finally(() => setConfirmDiscard(false))
              }}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Discard'}
            </Button>
          </>
        ) : (
          <>
            <Button
              type="button"
              size="sm"
              className="flex-1"
              onClick={onResume}
              disabled={disabled}
              data-testid="held-cart-resume"
            >
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Play className="mr-1.5 h-4 w-4" />}
              Resume
            </Button>
            <Button
              type="button"
              size="icon"
              variant="outline"
              className="h-9 w-9 shrink-0 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
              onClick={() => setConfirmDiscard(true)}
              disabled={disabled}
              aria-label="Discard held order"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
